import axios from 'axios'
import type { ComponentProps } from 'react'
import React, { useState } from 'react'
import { StringParam, useQueryParam } from 'use-query-params'

import { useAuth } from '@fourthwall/auth'
import type { Banner, Tag } from '@fourthwall/components'
import { ConfirmModal, Toasts, useModalContext } from '@fourthwall/components'
import { CreatorIntegrationsApiClient } from '@fourthwall/services/dist/CreatorIntegrationsApiClient'
import { getDefaultErrorMessage } from '@fourthwall/utils/dist/queryClient'
import { pluralize } from '@fourthwall/utils/dist/string'
import { useOffersTotal } from '@hooks'
import { useIsPaymentMethodModalRequiredMutation } from '@mutations/useIsPaymentMethodModalRequiredMutation'

import { PaymentMethodRequiredModal } from '../../../../../modules/PaymentMethods'
import type { ConnectBar } from '../../../components/ConnectBar/ConnectBar'
import type { ProductsStatusModalProps } from '../../../components/ProductsStatusModal/ProductsStatusModal'
import { metaConfigurationStatusTagMap } from '../AppsMeta.consts'
import type {
  Configure,
  MetaCheckoutUrls,
  MetaConfiguration,
  Products,
  Settings,
} from '../AppsMeta.types'
import { DisconnectErrorModal } from '../components/DisconnectErrorModal/DisconnectErrorModal'
import { useDeleteMetaConfigurationMutation } from '../queries/useDeleteMetaConfigurationMutation'
import { useMetaCatalogMutation } from '../queries/useMetaCatalogMutation'
import { useMetaCatalogsQuery } from '../queries/useMetaCatalogsQuery'
import { useMetaConfigurationQuery } from '../queries/useMetaConfigurationQuery'
import { useMetaProductsQuery } from '../queries/useMetaProductsQuery'
import { useMetaStatusMutation } from '../queries/useMetaStatusMutation'
import { createPreviewProps } from '../utils/createPreviewProps'

const CRITICAL_META_ERROR_REASONS = [
  'INVALID_AUTHORIZATION_TOKEN',
  'CATALOG_DOES_NOT_EXISTS',
] as const

export const useAppsMeta = () => {
  const { keycloak } = useAuth()
  const { open } = useModalContext()
  const [connectedParam] = useQueryParam('connected', StringParam)
  const [connecting, setConnecting] = useState(false)
  const offersTotal = useOffersTotal()
  const { metaConfigurationQuery, invalidateMetaConfigurationQuery } = useMetaConfigurationQuery(
    undefined,
    {
      refetchInterval: (data) => {
        if (!data) return false

        const { type } = data

        // returned from meta but needs one more refetch to get the correct status
        // also, !!connectedParam does not work because it's an empty string when having `?connected=#_=_` in URL
        if (type === 'NOT_CONNECTED' && typeof connectedParam === 'string') return 1_000

        return false
      },
    },
  )
  const { metaProductsQuery, invalidateMetaProductsQuery } = useMetaProductsQuery(undefined, {
    enabled:
      metaConfigurationQuery.isSuccess &&
      metaConfigurationQuery.data.type === 'CONFIGURED' &&
      metaConfigurationQuery.data.status === 'ACTIVE',
  })
  const { deleteMetaConfigurationMutation } = useDeleteMetaConfigurationMutation()
  const { metaStatusMutationMutation } = useMetaStatusMutation()
  const { isPaymentMethodModalRequiredMutation } = useIsPaymentMethodModalRequiredMutation()
  const { metaCatalogsQuery } = useMetaCatalogsQuery(undefined, {
    enabled:
      metaConfigurationQuery.isSuccess &&
      ['CONFIGURED', 'NOT_CONFIGURED'].includes(metaConfigurationQuery.data.type),
  })
  const { updateMetaCatalogMutation } = useMetaCatalogMutation()
  const [metaCatalogId, setMetaCatalogId] = useState('')

  const invalidateQueries = () => {
    invalidateMetaConfigurationQuery()
    invalidateMetaProductsQuery()
  }

  const setConfigurationStatus = async (status: 'ACTIVE' | 'INACTIVE') => {
    return await metaStatusMutationMutation.mutateAsync([{ status }], {
      onSuccess: invalidateQueries,
      onError: (error: unknown) => {
        Toasts.notify(getDefaultErrorMessage(error), {
          type: 'error',
        })
      },
    })
  }

  const onConnect = () => {
    setConnecting(true)

    const url = CreatorIntegrationsApiClient.appAuthorizationUrl({
      app: 'instagram-partner',
      token: keycloak?.token,
    })

    window.open(url)
  }

  const onDisconnect = () => {
    open(ConfirmModal, {
      title: 'Are you sure you want to disconnect from Instagram & Facebook Shop?',
      confirmLabel: 'Yes, disconnect',
      confirmAppearance: 'destructive',
      onConfirm: async () => {
        await deleteMetaConfigurationMutation.mutateAsync([], {
          onSuccess: invalidateQueries,
          onError: (error: unknown) => {
            if (
              axios.isAxiosError(error) &&
              error.response?.data.code === 'META_PARTNER_ORDERS_NOT_COMPLETED'
            ) {
              open(DisconnectErrorModal, {
                isEnabled:
                  metaConfigurationQuery.data?.type === 'CONFIGURED' &&
                  metaConfigurationQuery.data?.status === 'ACTIVE',
                onDisable: () => {
                  setConfigurationStatus('INACTIVE')
                },
              })

              return
            }

            Toasts.notify(getDefaultErrorMessage(error), {
              type: 'error',
            })
          },
        })
      },
    })
  }

  const getStatusTagProps = (): ComponentProps<typeof Tag> | undefined => {
    if (!metaConfigurationQuery.isSuccess) return

    return metaConfigurationStatusTagMap[metaConfigurationQuery.data.type]
  }

  const getConnectBarProps = (): Omit<ComponentProps<typeof ConnectBar>, 'logoSrc'> | undefined => {
    if (!metaConfigurationQuery.isSuccess) return

    const connectBarPropsMap: Record<
      MetaConfiguration['type'],
      Omit<ComponentProps<typeof ConnectBar>, 'logoSrc'>
    > = {
      NOT_CONNECTED: {
        description: 'Connect your Meta account',
        isConnecting: connecting,
        onConnect,
        disabled: false,
      },
      CONFIGURED: {
        description: 'Meta account',
        onDisconnect,
        onReconnect: onConnect,
      },
      NOT_CONFIGURED: {
        description: 'Meta account',
        onDisconnect,
        onReconnect: onConnect,
      },
    }

    return connectBarPropsMap[metaConfigurationQuery.data.type]
  }

  const products = ((): Products | undefined => {
    if (!offersTotal || !metaConfigurationQuery.isSuccess) return

    // It's not perfect to check it here. It just waits for the EmptyProductsApprovalStatus component to load.
    if (offersTotal.public === 0) {
      return { type: 'EMPTY' }
    }

    const shouldDisplayProducts =
      metaConfigurationQuery.data.type === 'CONFIGURED' &&
      metaConfigurationQuery.data.status === 'ACTIVE'

    if (!shouldDisplayProducts) return

    if (!metaProductsQuery.isSuccess) {
      return { type: 'LOADING' }
    }

    const { active, pending, denied } = metaProductsQuery.data

    const mapProduct = (
      product:
        | NonNullable<typeof metaProductsQuery.data.active>[number]
        | NonNullable<typeof metaProductsQuery.data.pending>[number]
        | NonNullable<typeof metaProductsQuery.data.denied>[number],
    ): ProductsStatusModalProps['products'][number] => ({
      id: product.offerId,
      imageSrc: product.imageUrl,
      name: product.name,
      description: '', // overrides the default description
      variants: product.issues.map((issue) => ({
        description: (
          <>
            {issue.detail}.
            {issue.documentation && (
              <>
                {' '}
                <a href={issue.documentation} target="_blank" rel="noreferrer">
                  Learn more
                </a>
              </>
            )}
          </>
        ),
      })),
    })

    const isNonNullable = <TItem extends {} | boolean | undefined>(
      item: TItem,
    ): item is Exclude<NonNullable<TItem>, false> => {
      return Boolean(item)
    }

    const errorSamples = (() => {
      if (!metaConfigurationQuery.isSuccess || !metaProductsQuery.isSuccess) return

      if (metaConfigurationQuery.data.type !== 'CONFIGURED') return

      const { errorSamples } = metaProductsQuery.data

      if (!errorSamples || !errorSamples.length) return

      const { metaProductCatalogId } = metaConfigurationQuery.data

      if (!metaProductCatalogId) return

      return {
        type: 'pending' as const,
        label: `${errorSamples.length} ${pluralize(errorSamples.length, 'product')} with issues to resolve`,
        details: {
          type: 'LINK' as const,
          url: `https://business.facebook.com/commerce/catalogs/${metaProductCatalogId}/products/`,
        },
      }
    })()

    return {
      type: 'PRODUCTS',
      groups: [
        active &&
          !!active.length && {
            type: 'approved' as const,
            label: `${active.length} ${pluralize(active.length, 'product')} approved`,
            details: { type: 'ITEMS' as const, items: active.map(mapProduct) },
          },
        pending &&
          !!pending.length && {
            type: 'pending' as const,
            label: `${pending.length} ${pluralize(pending.length, 'product')} pending`,
            details: { type: 'ITEMS' as const, items: pending.map(mapProduct) },
          },
        errorSamples,
        denied &&
          !!denied.length && {
            type: 'rejected' as const,
            label: `${denied.length} ${pluralize(denied.length, 'product')} rejected`,
            details: { type: 'ITEMS' as const, items: denied.map(mapProduct) },
          },
      ].filter(isNonNullable),
    }
  })()

  const openPaymentMethodRequiredModal = () => {
    open(PaymentMethodRequiredModal, {
      title: 'Payment method required',
      subtitle: (
        <>
          For Facebook or Instagram Shop orders, Meta collects the money from the sale and sends you
          the profit directly. Fourthwall will deduct any costs of Meta orders (such as
          manufacturing or shipping we’re handling) from your account balance, and if your account
          balance does not cover the costs we will charge the amount to your payment method.
        </>
      ),
      onSuccess: () => setConfigurationStatus('ACTIVE'),
    })
  }

  const activateSales = async () => {
    return await isPaymentMethodModalRequiredMutation.mutateAsync([], {
      onSuccess: (data) => {
        if (data.isPaymentMethodModalRequired) {
          openPaymentMethodRequiredModal()
        } else {
          setConfigurationStatus('ACTIVE')
        }
      },
    })
  }

  const banners: ComponentProps<typeof Banner>[] = (() => {
    if (!metaConfigurationQuery.isSuccess || metaConfigurationQuery.data.type !== 'CONFIGURED') {
      return []
    }

    return [
      ...metaConfigurationQuery.data.reasons.map((reason) => ({
        title: reason.text,
        appearance: 'danger' as const,
        active: CRITICAL_META_ERROR_REASONS.includes(reason.type),
      })),
    ].filter((banner) => banner.active) satisfies NonNullable<Settings>['banners']
  })()

  const settings: Settings | undefined = (() => {
    if (!metaConfigurationQuery.isSuccess) return

    const { type } = metaConfigurationQuery.data

    if (type !== 'CONFIGURED') return

    const { status, reasons } = metaConfigurationQuery.data

    return {
      banners,
      initialValues: {
        status: status === 'ACTIVE',
      },
      disabled:
        status === 'INACTIVE' &&
        reasons.some((reason) => CRITICAL_META_ERROR_REASONS.includes(reason.type)),
      onSubmit: async (values) => {
        if (values.status) {
          await activateSales()

          return
        }

        return setConfigurationStatus('INACTIVE')
      },
    }
  })()

  const configure: Configure | undefined = (() => {
    if (!metaConfigurationQuery.isSuccess || !metaCatalogsQuery.isSuccess) return

    const { type } = metaConfigurationQuery.data

    if (type === 'NOT_CONNECTED') {
      return { type: 'CONNECT_FIRST' }
    }

    if (type === 'CONFIGURED') return

    const { catalogs, commerceManagerOnboardingUri } = metaCatalogsQuery.data

    const options = catalogs.map<{ label: string; value: string }>((catalog) => ({
      label: catalog.name,
      value: catalog.id,
    }))

    if (!options.length) {
      return {
        type: 'CREATE_SHOP',
        url: commerceManagerOnboardingUri,
      }
    }

    // Find the selected catalog to check if it's used in another shop
    const selectedCatalog = catalogs.find((catalog) => catalog.id === metaCatalogId)
    const isSelectedCatalogUsedInAnotherShop = selectedCatalog?.isUsedInAnotherShop ?? false

    return {
      type: 'SELECT_SHOP',
      url: commerceManagerOnboardingUri,
      selectProps: { options, onValueChange: setMetaCatalogId },
      previewProps: createPreviewProps(metaCatalogId, metaCatalogsQuery.data),
      isSelectedCatalogUsedInAnotherShop,
      onSubmit: async ({ metaCatalogId }) => {
        return await updateMetaCatalogMutation.mutateAsync(
          [{ metaProductCatalogId: metaCatalogId }],
          {
            onSuccess: () => {
              invalidateQueries()
              activateSales()
            },
          },
        )
      },
    }
  })()

  const checkoutUrls: MetaCheckoutUrls | undefined = (() => {
    if (!metaConfigurationQuery.isSuccess) return

    const { type } = metaConfigurationQuery.data

    if (type !== 'CONFIGURED') return

    const { metaCommerceCheckoutSetupUri, fwCheckoutRedirectUri } = metaConfigurationQuery.data

    if (metaCommerceCheckoutSetupUri == undefined) return

    return {
      metaCommerceCheckoutSetupUri,
      fwCheckoutRedirectUri,
    }
  })()

  return {
    isLoading: metaConfigurationQuery.isLoading,
    statusTagProps: getStatusTagProps(),
    connectBarProps: getConnectBarProps(),
    products,
    settings,
    configure,
    checkoutUrls,
    metaConfigurationQuery,
  }
}
